/** Where an export goes: a download from PRISM (kept for a while), or one of the export locations. */
import type { ExportDestinationDTO } from '@prism/contracts';

const downloadLabel = (d: ExportDestinationDTO) =>
  `Download from PRISM (${d.retentionDays == null ? 'kept until deleted' : `kept ${d.retentionDays} day${d.retentionDays === 1 ? '' : 's'}`})`;

export function DestinationSelect({ id, value, onChange, destinations }: { id: string; value: string; onChange: (v: string) => void; destinations: ExportDestinationDTO[] }) {
  const chosen = destinations.find((d) => d.id === value) ?? destinations.find((d) => d.isDefault);
  return (
    <>
      <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value)} data-testid="export-destination">
        {destinations.filter((d) => d.isDefault).map((d) => <option key={d.id} value="">{downloadLabel(d)}</option>)}
        {destinations.some((d) => !d.isDefault) && (
          <optgroup label="Export locations">
            {destinations.filter((d) => !d.isDefault).map((d) => <option key={d.id} value={d.id}>{d.name} · {d.site}{d.availability === 'offline' ? ' (offline)' : ''}</option>)}
          </optgroup>
        )}
      </select>
      <span className="help">
        {chosen?.isDefault
          ? 'Download it from PRISM when it is ready. Administrators add export locations (buckets, server folders, folders on storage agents) under Administration → Export locations.'
          : chosen?.delivered ? 'A folder on a storage agent: the export renders centrally and is then copied to the site.' : 'Written straight into this export location.'}
      </span>
    </>
  );
}

/** "Download until 2 Nov", "Download expired" — for export lists. */
export function downloadNote(d: { expiresAt: string | null; expired: boolean } | null): string | null {
  if (!d) return null;
  if (d.expired) return 'download expired';
  if (!d.expiresAt) return null;
  return `download until ${new Date(d.expiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
}
